import * as React from 'react';

import { PageProps, graphql } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import hubLibrary from 'stile-shared/assets/images/case-studies/hub-library.jpg';
import hubMicroscope from 'stile-shared/assets/images/case-studies/hub-microscope.jpg';
import hubSafetyGoggles from 'stile-shared/assets/images/case-studies/hub-safety-goggles.jpg';
import hubStileX from 'stile-shared/assets/images/case-studies/hub-stile-x.jpg';
import hubStudentWriting from 'stile-shared/assets/images/case-studies/hub-student-writing.jpg';
import hubTablet from 'stile-shared/assets/images/case-studies/hub-tablet.jpg';
import hubTeacherPortrait from 'stile-shared/assets/images/case-studies/hub-teacher-portrait.jpg';

import CaseStudiesComponent from 'stile-shared/src/components/2024/common/CaseStudies/CaseStudyList';
import FeaturedCaseStudy from 'stile-shared/src/components/2024/common/CaseStudies/FeaturedCaseStudy';
import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import TitleMenu from 'stile-shared/src/components/2024/common/Included/WITBTitleMenu/WITBTitleMenu';
import Top from 'stile-shared/src/components/2024/common/Included/WITBTopSection/WITBTopSection';
import ReviewsSlider from 'stile-shared/src/components/2024/layout/ReviewsSlider/ReviewsSlider';
import VideoGallery from 'stile-shared/src/components/2024/layout/VideoGallery';

import { PageLayout } from 'templates/2024/PageLayout';
import { SEO } from 'templates/2024/SEO';

import { allCaseStudies } from '../../../data/caseStudies';

const ContentWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  overflow: hidden;
  color: var(--white);
  .bodyHeader {
    margin: 0 0 40px;
    color: var(--stile-grey-8);
    text-align: center;
  }
  .teachers {
    padding-bottom: 134px;
  }
  @media (max-width: 768px) {
    padding-bottom: 0px;
    padding-top: 40px;
    .bodyHeader {
      margin: 0 25px 40px;
    }
    .teachers {
      padding-bottom: 60px;
    }
  }
`;

const Container = styled.div`
  max-width: 1160px;
  padding: 0 0 90px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 1220px) {
    margin: 0 60px;
    max-width: none;
  }
  @media (max-width: 768px) {
    margin: 0;
    padding: 0 25px 29px;
  }
`;

const TitleMenuContainer = styled.div`
  display: flex !important;
  flex-direction: column !important;
  padding-bottom: 80px !important;
  /* Feature Heading in the designs. WITBTopSection hardcodes 16px/400 on its
     own p, which out-specifies the global .--feature_heading class, so the
     token's values are restated here. */
  p {
    max-width: 563px;
    font-size: 21px;
    font-weight: 500;
    line-height: 26px;

    @media (max-width: 768px) {
      font-size: 19px;
      line-height: 24px;
    }
  }
  h1 {
    max-width: 563px;
    align-self: center;
    @media (max-width: 768px) {
      max-width: 300px;
    }
  }
  .content {
    padding-top: 0px !important;
    @media (max-width: 768px) {
      .grid-layout {
        margin-top: 28px !important;
      }
      padding-top: 0px !important;
    }
  }
  @media (max-width: 768px) {
    padding: 0 25px 20px !important;
    margin: 0 !important;
    > div > div {
      padding-top: 17px !important;
    }
  }
`;

export function Head() {
  return (
    <SEO
      slug="/why-choose-stile/case-studies"
      description="Teachers are using Stile to boost engagement, deepen understanding and achieve better outcomes."
      title="Case Studies | Stile"
      opengraphTitle="Case Studies | Stile"
      opengraphDescription="Teachers are using Stile to boost engagement, deepen understanding and achieve better outcomes."
    />
  );
}

type DataProps = {
  allCaseStudyImages: {
    nodes: Array<{
      base: string;
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData;
      };
    }>;
  };
};

function CaseStudiesPage({ data, ...props }: PageProps<DataProps>) {
  const imageMap = data.allCaseStudyImages.nodes.reduce<Record<string, IGatsbyImageData>>(
    (acc, { base, childImageSharp: { gatsbyImageData } }) => {
      acc[base] = gatsbyImageData;
      return acc;
    },
    {}
  );

  const enrichedCaseStudies = allCaseStudies.map((cs) => {
    const filename = cs.image.split('/').pop();
    if (!filename) {
      throw new Error(`Invalid image path: ${cs.image}`);
    }
    const imgData = imageMap[filename];
    if (!imgData) {
      throw new Error(`Case study image missing from GraphQL: ${filename}`);
    }
    return {
      ...cs,
      // The hub tiles and the featured card carry the designs' shorter copy;
      // the longer `description` stays on the detail page.
      description: cs.tileDescription ?? cs.description,
      imageData: imgData,
    };
  });

  const featureIndex = 0;
  const featured = enrichedCaseStudies[featureIndex];

  const exploreCards = [
    {
      title: 'Explicit Instruction',
      description: 'Clear teacher modelling and guided practice in every Stile lesson.',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      title: 'Stile X Workbooks',
      description:
        'Designed for hands-on learning and perfectly matched to the digital curriculum.',
      url: '/what-is-stile/stile-x/',
    },
    {
      title: 'Our Guarantees & Principles',
      description: 'Some talk the talk, we walk the walk. Discover our non-negotiables.',
      url: '/who-we-are/our-principles/',
    },
  ];

  const exploreCurriculumImage = (
    <StaticImage
      src="../../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  return (
    <PageLayout {...props}>
      <TitleMenuContainer className="content">
        <TitleMenu>
          <Top
            title="Real classrooms, real impact"
            description="Teachers are using Stile to boost engagement, deepen understanding and achieve better outcomes."
          />
        </TitleMenu>
      </TitleMenuContainer>
      <ContentWrapper>
        <VideoGallery
          className="teachers"
          videoAspectRatio="1"
          // Order follows the Hero_AU frame in Figma, left to right, with the
          // hero (index 3) in the centre column.
          items={[
            { type: 'img', url: hubTeacherPortrait },
            { type: 'img', url: hubMicroscope },
            { type: 'img', url: hubSafetyGoggles },
            { type: 'img', url: hubStileX },
            { type: 'img', url: hubLibrary },
            { type: 'img', url: hubTablet },
            { type: 'img', url: hubStudentWriting },
          ]}
        />
        <h2 className="bodyHeader">Hear from our teachers</h2>
      </ContentWrapper>
      <FeaturedCaseStudy
        id={featured.slug}
        title={featured.title}
        subtitle={featured.subtitle}
        description={featured.description}
        url={`/why-choose-stile/case-studies/${featured.slug}`}
        youtubeUrl={featured.youtube}
        media={<GatsbyImage image={featured.imageData} alt={featured.title} />}
      />
      <Container>
        <CaseStudiesComponent
          caseStudies={enrichedCaseStudies}
          featureIndex={featureIndex}
          basePath="/why-choose-stile/case-studies"
        />
      </Container>
      <ReviewsSlider />
      <ExploreCurriculum
        title="Explore our core science curriculum"
        exploreCardsData={exploreCards}
        image={exploreCurriculumImage}
      />
    </PageLayout>
  );
}

export const query = graphql`
  query AuCaseStudyImages {
    allCaseStudyImages: allFile(filter: { relativePath: { glob: "case-studies/*.jpg" } }) {
      nodes {
        base
        childImageSharp {
          gatsbyImageData(width: 800, placeholder: BLURRED, quality: 80)
        }
      }
    }
  }
`;

export default CaseStudiesPage;
